import * as React from 'react';
import { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { Switch } from 'Src/components/Switch/Switch';
import { DEFAULT_PUBLIC_SITE, KIND } from 'Src/consts/school';
import { updateSchoolSettings } from 'Src/helpers/service/admin/settings';
import { checkDomainAvailabilty } from 'Src/helpers/service/public/public';
import { SchoolSettingsTabs } from '../SchoolSettingsTabs';
import './PublicSiteSettings.scss';

interface Props {
  user: AppUser;
  onSchoolUpdate: () => Promise<any>;
}

interface PageDefinition {
  key: string;
  name: string;
  note: string;
  icon: string;
  comingSoon?: boolean;
  required?: boolean;
}

const PAGE_DEFINITIONS: PageDefinition[] = [
  { key: 'home', name: 'Home', note: 'The main landing page and starting point for the public site', icon: 'M3 10.5 12 4l9 6.5V20H15v-6h-6v6H3z', required: true },
  { key: 'calendar', name: 'Calendar', note: 'Month grid of fixtures, clubs and tournaments', icon: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4' },
  { key: 'fixturesAndResults', name: 'Fixtures and results', note: 'Filterable listings, grouped day by day', icon: 'M4 6h16M4 12h16M4 18h10' },
  { key: 'teams', name: 'Teams', note: 'One page per team: squad, fixtures and results', icon: 'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 20c0-3 2.7-5 6-5s6 2 6 5M16 6a3 3 0 0 1 0 6M18 20c0-2.4-1-4.2-2.6-5.2', comingSoon: true },
  { key: 'records', name: 'Records', note: 'School honours boards by sport and season', icon: 'M8 4h8v5a4 4 0 0 1-8 0zM6 4H3v2c0 3 2 5 5 5M18 4h3v2c0 3-2 5-5 5M12 13v5M8 21h8' },
  { key: 'photos', name: 'Photos', note: 'Albums whose photos are set to public', icon: 'M4 6h4l1.5-2h5L16 6h4v14H4zM12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z' },
  { key: 'tournaments', name: 'Tournaments', note: 'Published school tournaments and booking details', icon: 'M8 4h8v5a4 4 0 0 1-8 0zM6 4H3v2c0 3 2 5 5 5M18 4h3v2c0 3-2 5-5 5M12 13v5M8 21h8' },
  { key: 'leagues', name: 'Leagues', note: 'Published team and house leagues', icon: 'M4 5h16v14H4zM8 9h8M8 13h8M8 17h5' },
  { key: 'challenges', name: 'Challenges', note: 'Published individual challenges', icon: 'M5 20V10M12 20V4M19 20v-7' },
  { key: 'clubEvents', name: 'Club events', note: 'Lunchtime and after-school club events', icon: 'M8 3h8v4a4 4 0 0 1-8 0zM6 21h12M12 11v10' },
  { key: 'news', name: 'News', note: 'Published school stories and announcements', icon: 'M4 5h16v14H4zM8 9h5M8 13h8M8 17h8' },
  { key: 'housePoints', name: 'House points', note: 'Current season table across school houses', icon: 'M5 20V10M12 20V4M19 20v-7' },
  { key: 'search', name: 'Search', note: 'Search events, teams, news and records', icon: 'M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14zM16 16l5 5', comingSoon: true }
];

const UNION_PAGE_DEFINITIONS: PageDefinition[] = [
  { key: 'home', name: 'Home', note: 'The main landing page and starting point for the public site', icon: 'M3 10.5 12 4l9 6.5V20H15v-6h-6v6H3z', required: true },
  { key: 'calendar', name: 'Calendar', note: 'Tournament calendar across this union and its areas', icon: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4' },
  { key: 'tournaments', name: 'Tournaments', note: 'Upcoming published tournaments and online booking', icon: 'M8 4h8v5a4 4 0 0 1-8 0zM6 4H3v2c0 3 2 5 5 5M18 4h3v2c0 3-2 5-5 5M12 13v5M8 21h8' },
  { key: 'tournamentResults', name: 'Results', note: 'Completed tournaments and their results', icon: 'M5 20V10M12 20V4M19 20v-7' },
  { key: 'records', name: 'Records', note: 'Latest union records with historical performances', icon: 'M8 4h8v5a4 4 0 0 1-8 0zM6 4H3v2c0 3 2 5 5 5M18 4h3v2c0 3-2 5-5 5M12 13v5M8 21h8' },
  { key: 'memberSchools', name: 'Member schools', note: 'Searchable directory of member schools', icon: 'M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 20c0-3 2.7-5 6-5s6 2 6 5M16 6a3 3 0 0 1 0 6M18 20c0-2.4-1-4.2-2.6-5.2' },
  { key: 'venues', name: 'Venues', note: 'Published tournament locations and maps', icon: 'M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6z' },
  { key: 'photos', name: 'Photos', note: 'Public union albums and tournament moments', icon: 'M4 6h4l1.5-2h5L16 6h4v14H4zM12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z' },
  { key: 'news', name: 'News', note: 'Published union news and announcements', icon: 'M4 5h16v14H4zM8 9h5M8 13h8M8 17h8' },
  { key: 'resources', name: 'Resources', note: 'Published documents and downloads', icon: 'M4 5h16v14H4zM8 9h8M8 13h8M8 17h5' }
];

const BRAND_SWATCHES = ['#164170', '#087F8C', '#2F6B3C', '#7A4EAB', '#A23B72', '#B4541B'];

const PRIVACY_DEFINITIONS = [
  {
    key: 'publicStudentViewType',
    name: 'Pupil names in squads and results',
    note: 'Uses the same pupil-name setting as team views for signed-in users.',
    options: [
      ['SHORTEN_FIRSTNAME_AND_LASTNAME', 'Initials only'],
      ['SHORTEN_LASTNAME_ONLY', 'First name & initial']
    ]
  },
];

const mergePublicSiteSettings = (publicSite, isUnion = false) => {
  const pages: any = propz.get(publicSite, ['pages'], {});
  const legacyCollections = typeof pages.collections === 'boolean' ? pages.collections : true;

  return {
    ...DEFAULT_PUBLIC_SITE,
    ...(publicSite || {}),
    pages: {
      ...DEFAULT_PUBLIC_SITE.pages,
      ...(isUnion ? { records: true, photos: true, tournamentResults: true, memberSchools: true, venues: true, resources: true } : {}),
      ...pages,
      tournaments: typeof pages.tournaments === 'boolean' ? pages.tournaments : legacyCollections,
      leagues: typeof pages.leagues === 'boolean' ? pages.leagues : legacyCollections,
      challenges: typeof pages.challenges === 'boolean' ? pages.challenges : legacyCollections
    },
    appearance: {
      ...DEFAULT_PUBLIC_SITE.appearance,
      ...propz.get(publicSite, ['appearance'], {})
    },
    privacy: {
      ...DEFAULT_PUBLIC_SITE.privacy,
      ...propz.get(publicSite, ['privacy'], {})
    }
  };
};

const PageIcon = ({ path }: { path: string }) => (
  <span className="publicSitePageIcon" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none"><path d={path} /></svg>
  </span>
);

export const PublicSiteSettings: React.FunctionComponent<Props> = props => {
  const { user, onSchoolUpdate } = props;
  const school = user.activeSchool;
  const isUnion = school.kind === KIND.SCHOOL_UNION;
  const pageDefinitions = isUnion ? UNION_PAGE_DEFINITIONS : PAGE_DEFINITIONS;
  const [isSaving, setIsSaving] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const initialValues = useMemo(() => ({
    domain: String(propz.get(school, ['domain'], '') || ''),
    publicStudentViewType: propz.get(school, ['publicStudentViewType'], 'SHORTEN_LASTNAME_ONLY'),
    publicSite: mergePublicSiteSettings(propz.get(school, ['publicSite'], {}), isUnion)
  }), [isUnion, school]);

  const isIncludedInPlan = school.canPublishWebSite !== false;
  const schoolLogo = school.pic || '';
  const schoolName = school.name || (isUnion ? 'Your union' : 'Your school');

  const saveSettings = async (values, helpers) => {
    setError('');
    setSavedMessage('');

    const domain = String(values.domain || '').trim().toLowerCase();
    const previousDomain = String(propz.get(school, ['domain'], '') || '').trim().toLowerCase();

    if (values.publicSite.status !== 'DISABLED' && !domain) {
      setError('Add a site address before publishing the public website.');
      return;
    }
    if (values.publicSite.status === 'PROTECTED' && !String(values.publicSite.password || '').trim()) {
      setError('Add a shared password before password-protecting the site.');
      return;
    }

    setIsSaving(true);
    try {
      if (domain && domain !== previousDomain) {
        const availability = await checkDomainAvailabilty(user, domain);
        if (!availability.isAvailable) {
          setError('This site address is already in use. Choose another one.');
          setIsSaving(false);
          return;
        }
      }

      const publicSite = {
        ...values.publicSite,
        configurationVersion: 3,
        pages: {
          ...values.publicSite.pages,
          home: true,
          collections: Boolean(
            values.publicSite.pages.tournaments ||
            values.publicSite.pages.leagues ||
            values.publicSite.pages.challenges
          )
        }
      };
      await updateSchoolSettings(user, {
        domain: domain || null,
        publicStudentViewType: values.publicStudentViewType,
        publicSite
      });
      await onSchoolUpdate();
      helpers.resetForm({ values: { ...values, domain, publicSite } });
      setSavedMessage('Public site settings published.');
    } catch (saveError) {
      setError('The public site settings could not be saved. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isSaving) {
    return <Loader />;
  }

  return (
    <div className="schoolSettingsPage publicSiteSettingsPage">
      <header className="schoolSettingsHeader">
        <h1>{isUnion ? 'Public site' : 'School settings'}</h1>
        <SchoolSettingsTabs isUnion={isUnion} />
      </header>

      {error && <div className="schoolSettingsAlert schoolSettingsAlert--danger">{error}</div>}
      {savedMessage && <div className="schoolSettingsAlert schoolSettingsAlert--success">{savedMessage}</div>}

      <Formik initialValues={initialValues} enableReinitialize onSubmit={saveSettings}>
        {({ values, setFieldValue, dirty, resetForm, handleSubmit }) => {
          const status = values.publicSite.status;
          const brandColor = values.publicSite.appearance.brandColor || '#164170';
          const siteAddress = values.domain ? `${values.domain}.squadintouch.com` : 'Choose your site address';

          return (
            <Form className="publicSiteSettingsForm">
              {!isIncludedInPlan && (
                <section className="publicSitePlanNotice">
                  <div>
                    <strong>Public website is not included in the current plan</strong>
                    <span>You can prepare the site and preview its appearance, but it cannot be published yet.</span>
                  </div>
                  <span className="publicSiteBadge publicSiteBadge--soon">Plan upgrade required</span>
                </section>
              )}

              <div className="publicSiteSettingsLayout">
                <main className="publicSiteSettingsMain">
                  <section className="publicSiteCard publicSiteStatusCard">
                    <div className="publicSiteCardHeader">
                      <div>
                        <h2>Public site status</h2>
                        <p>Choose who can open the site and where it is published.</p>
                      </div>
                      <span className={`publicSiteBadge publicSiteBadge--${status === 'PUBLIC_AVAILABLE' ? 'live' : status === 'PROTECTED' ? 'protected' : 'off'}`}>
                        {status === 'PUBLIC_AVAILABLE' ? 'Live' : status === 'PROTECTED' ? 'Protected' : 'Off'}
                      </span>
                    </div>

                    <div className="publicSiteStatusOptions" role="radiogroup" aria-label="Public site status">
                      {[
                        ['PUBLIC_AVAILABLE', 'Live', 'Anyone with the link can view the site.'],
                        ['PROTECTED', 'Password protected', 'Visitors enter a shared password first.'],
                        ['DISABLED', 'Off', 'The public site is not available.']
                      ].map(option => (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={status === option[0]}
                          className={`publicSiteStatusOption ${status === option[0] ? 'publicSiteStatusOption--selected' : ''}`}
                          onClick={() => setFieldValue('publicSite.status', option[0])}
                          key={option[0]}
                        >
                          <span className="publicSiteStatusOption__dot" />
                          <span><strong>{option[1]}</strong><small>{option[2]}</small></span>
                        </button>
                      ))}
                    </div>

                    <div className="publicSiteAddressGrid">
                      <label className="publicSiteField">
                        <span>Site address</span>
                        <span className="publicSiteDomainInput">
                          <input
                            value={values.domain}
                            onChange={event => setFieldValue('domain', event.target.value.replace(/[^a-zA-Z0-9-]/g, ''))}
                            placeholder={isUnion ? 'your-union' : 'your-school'}
                          />
                          <span>.squadintouch.com</span>
                        </span>
                      </label>
                      <div className="publicSiteField publicSiteField--disabled">
                        <span>Custom domain</span>
                        <div className="publicSiteComingSoonField">
                          <span>{isUnion ? 'www.yourunion.org' : 'www.yourschool.org'}</span>
                          <span className="publicSiteBadge publicSiteBadge--soon">Coming soon</span>
                        </div>
                      </div>
                    </div>

                    {status === 'PROTECTED' && (
                      <label className="publicSiteField publicSitePasswordField">
                        <span>Shared password</span>
                        <input
                          type="text"
                          value={values.publicSite.password || ''}
                          onChange={event => setFieldValue('publicSite.password', event.target.value)}
                          placeholder="Enter a password visitors can use"
                        />
                      </label>
                    )}
                  </section>

                  <section className="publicSiteCard">
                    <div className="publicSiteCardHeader">
                      <div><h2>Pages</h2><p>Control the sections available from the public-site navigation.</p></div>
                    </div>
                    <div className="publicSitePageRows">
                      {pageDefinitions.map(page => {
                        const enabled = Boolean(values.publicSite.pages[page.key]);
                        return (
                          <div className={`publicSitePageRow ${page.comingSoon ? 'publicSitePageRow--soon' : ''}`} key={page.key}>
                            <PageIcon path={page.icon} />
                            <div className="publicSitePageRow__copy">
                              <strong>{page.name}</strong>
                              <span>{page.note}</span>
                            </div>
                            {page.required ? (
                              <span className="publicSiteBadge publicSiteBadge--live">Always on</span>
                            ) : page.comingSoon ? (
                              <span className="publicSiteBadge publicSiteBadge--soon">Coming soon</span>
                            ) : (
                              <Switch
                                name={`publicSite.pages.${page.key}`}
                                checked={enabled}
                                onChange={() => setFieldValue(`publicSite.pages.${page.key}`, !enabled)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="publicSitePageFootnote">A published page still shows an intentional empty state when {isUnion ? 'the union' : 'the school'} has not added content yet.</p>
                  </section>

                  <section className="publicSiteCard">
                    <div className="publicSiteCardHeader">
                      <div><h2>Appearance</h2><p>One colour drives the site. We adjust it where necessary to keep text readable.</p></div>
                    </div>
                    <div className="publicSiteCardBody">
                      <div className="publicSiteFieldLabel">Brand colour</div>
                      <div className="publicSiteSwatches">
                        {BRAND_SWATCHES.map(color => (
                          <button
                            type="button"
                            className={brandColor.toLowerCase() === color.toLowerCase() ? 'publicSiteSwatch publicSiteSwatch--selected' : 'publicSiteSwatch'}
                            style={{ backgroundColor: color }}
                            aria-label={`Use brand colour ${color}`}
                            onClick={() => setFieldValue('publicSite.appearance.brandColor', color)}
                            key={color}
                          />
                        ))}
                        <label className="publicSiteCustomColour" title="Choose a custom colour">
                          <input type="color" value={brandColor} onChange={event => setFieldValue('publicSite.appearance.brandColor', event.target.value)} />
                          <span>Custom</span>
                        </label>
                      </div>

                      <div className="publicSiteMediaSettings">
                        <div>
                          <div className="publicSiteFieldLabel">{isUnion ? 'Union logo' : 'School crest'}</div>
                          <div className="publicSiteMediaSetting">
                            <span className="publicSiteCrest">{schoolLogo ? <img src={schoolLogo} alt="" /> : schoolName.slice(0, 2).toUpperCase()}</span>
                            <div><strong>Managed in Profile</strong><span>The same crest is used throughout Squad In Touch.</span></div>
                          </div>
                        </div>
                        <div>
                          <div className="publicSiteFieldLabel">Homepage slideshow</div>
                          <div className="publicSiteMediaSetting">
                            <span className="publicSiteHeaderPlaceholder">Gallery</span>
                            <div><strong>Managed in {isUnion ? 'Union gallery' : 'School gallery'}</strong><span>Public gallery photos are used automatically in the homepage header.</span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {!isUnion && <section className="publicSiteCard">
                    <div className="publicSiteCardHeader">
                      <div><h2>What is published about pupils</h2><p>These rules apply across desktop, mobile and future calendar feeds.</p></div>
                    </div>
                    <div className="publicSitePrivacyGroups">
                      {PRIVACY_DEFINITIONS.map(group => {
                        const isStudentNameSetting = group.key === 'publicStudentViewType';
                        const fieldName = isStudentNameSetting
                          ? 'publicStudentViewType'
                          : `publicSite.privacy.${group.key}`;
                        const selectedValue = isStudentNameSetting
                          ? values.publicStudentViewType
                          : values.publicSite.privacy[group.key];

                        return <div className="publicSitePrivacyGroup" key={group.key}>
                          <strong>{group.name}</strong>
                          <p>{group.note}</p>
                          <div className="publicSiteSegmented">
                            {group.options.map(option => (
                              <button
                                type="button"
                                className={selectedValue === option[0] ? 'publicSiteSegmented__item publicSiteSegmented__item--selected' : 'publicSiteSegmented__item'}
                                aria-pressed={selectedValue === option[0]}
                                onClick={() => setFieldValue(fieldName, option[0])}
                                key={option[0]}
                              >
                                {option[1]}
                              </button>
                            ))}
                          </div>
                        </div>;
                      })}
                    </div>
                  </section>}
                </main>

                <aside className="publicSiteSettingsAside">
                  <section className="publicSiteCard publicSitePreviewCard">
                    <div className="publicSitePreviewHeader">
                      <h2>Preview</h2>
                      <div className="publicSiteDeviceSwitch">
                        <button type="button" className={device === 'desktop' ? 'is-active' : ''} onClick={() => setDevice('desktop')}>Desktop</button>
                        <button type="button" className={device === 'phone' ? 'is-active' : ''} onClick={() => setDevice('phone')}>Phone</button>
                      </div>
                    </div>
                    <div className={`publicSitePreview publicSitePreview--${device}`} style={{ ['--preview-brand' as any]: brandColor }}>
                      <div className="publicSitePreview__nav">
                        <span className="publicSitePreview__crest">{schoolLogo ? <img src={schoolLogo} alt="" /> : schoolName.slice(0, 2).toUpperCase()}</span>
                        <span><strong>{schoolName}</strong><small>{isUnion ? 'Tournaments, results and member schools' : 'Sport, fixtures and school life'}</small></span>
                      </div>
                      <div className="publicSitePreview__body">
                        <span className="publicSitePreview__eyebrow">Next up</span>
                        <strong>{isUnion ? 'Next tournament' : 'First XI v St James School'}</strong>
                        <span>{isUnion ? 'Saturday · 10:30 · Regional venue' : 'Saturday · 10:30 · Home'}</span>
                        <div className="publicSitePreview__chips"><i>{isUnion ? 'Tournament' : 'Football'}</i><i>Senior</i><i>Mixed</i></div>
                        <div className="publicSitePreview__rows"><span /><span /><span /></div>
                      </div>
                    </div>
                    <p className="publicSitePreviewNote">Live preview of the selected colour and school identity. Page content previews will expand as each section is rebuilt.</p>
                  </section>

                  <section className="publicSiteCard publicSiteIndexingCard">
                    <div>
                      <h2>Search engines</h2>
                      <p>Allow public pages to be indexed by search engines.</p>
                    </div>
                    <Switch
                      name="publicSite.isSearchEngineIndexingEnabled"
                      checked={Boolean(values.publicSite.isSearchEngineIndexingEnabled)}
                      onChange={() => setFieldValue('publicSite.isSearchEngineIndexingEnabled', !values.publicSite.isSearchEngineIndexingEnabled)}
                      disabled={status !== 'PUBLIC_AVAILABLE'}
                    />
                  </section>

                  <section className="publicSiteAddressSummary">
                    <span>Public address</span>
                    <strong>{siteAddress}</strong>
                    <small>{status === 'DISABLED' ? 'The site is currently off.' : status === 'PROTECTED' ? 'Visitors will be asked for the shared password.' : 'The site is visible to everyone.'}</small>
                  </section>
                </aside>
              </div>

              {dirty && (
                <div className="publicSiteSaveBar" role="region" aria-label="Unsaved public site settings">
                  <div><strong>You have unpublished changes</strong><span>Visitors will continue to see the previous settings until you publish.</span></div>
                  <div className="publicSiteSaveBar__actions">
                    <button type="button" className="schoolSettingsButton schoolSettingsButton--secondary" onClick={() => { resetForm(); setError(''); }}>Discard</button>
                    <button type="button" className="schoolSettingsButton schoolSettingsButton--primary" disabled={!isIncludedInPlan && status !== 'DISABLED'} onClick={() => handleSubmit()}>Publish changes</button>
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

PublicSiteSettings.displayName = 'PublicSiteSettings';
